import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import {
  actions,
  isOwner,
  validateProjectName,
  validateProjectNameLooksLikeEmailSubject,
} from 'entities';

import { Row, IconButton } from 'components/Blocks';
import { HistoryIcon, PencilIcon } from 'components/Icons';

import { ProjectNameAdvisory } from 'pages/Projects/components';
import { ChangeHistoryModal } from './ChangeHistoryModal';
import { useProjectProfile } from '../../../context';

import { useJustification } from 'providers/Justification';
import { RequestConfirmationParams, useConfirmation } from 'providers/Confirmation';
import { useToggleV2, useUser } from 'hooks';

const propertyValidators: {
  [key: string]: {
    onChange: typeof validateProjectName;
    onSubmit: typeof validateProjectNameLooksLikeEmailSubject;

    parser: (value: string) => void;

    getConfirmationProps: (
      value: string,
      t: TFunction,
    ) => Pick<RequestConfirmationParams, 'title' | 'size' | 'text'>;
  };
} = {
  name: {
    onChange: validateProjectName,
    onSubmit: validateProjectNameLooksLikeEmailSubject,

    parser: (value: string) => {
      const allUnderscores = /_/g;
      const name = value.replace(allUnderscores, ' ');

      const multiWhiteSpaces = /(\s+)/g;

      return name.replace(multiWhiteSpaces, ' ').trim();
    },

    getConfirmationProps: (value: string, t) => ({
      title: t('confirm-project-name') as string,

      size: 'medium',

      text: <ProjectNameAdvisory name={value} />,
    }),
  },
};

export function ProjectName() {
  const { t } = useLang();
  const loggedUser = useUser();
  const { project, updateProject } = useProjectProfile();

  const [historyIsOpen, historyHandlers] = useToggleV2();

  const { openJustificationModal } = useJustification();
  const { requestConfirmation } = useConfirmation();

  const changedProps = project?.changedProperties || [];

  const hasHistory = changedProps.includes('name');

  const updateProjectProperty = actions.projects.updateProjectProperty.use();

  const handleEditClick = () => {
    const { onChange, onSubmit, getConfirmationProps } = propertyValidators['name'];

    openJustificationModal({
      text: t('why-name-change'),

      type: 'property',

      propertyValidation: (value) =>
        onChange({
          clients: [{ id: project.client, name: project.clientName }],
          project: {
            ...project,
            name: value,
          },
        }),

      async onConfirm(userReason, input) {
        const [justification, newValue] = [userReason, input].map((data) => `${data}`.trim());

        async function confirm() {
          const success = await updateProjectProperty.execute({
            justification,
            projectId: project.id,
            properties: {
              name: newValue,
            },
          });

          if (!success) return;

          updateProject((old) => ({
            ...old,

            name: newValue,

            changedProperties: [...(old.changedProperties ?? []), 'name'],
          }));
        }

        const requireConfirmation = onSubmit(`${input}`);

        if (!requireConfirmation) return confirm();

        requestConfirmation({
          onConfirm: confirm,

          ...getConfirmationProps(`${input}`, t),
        });
      },
    });
  };

  return (
    <>
      <ChangeHistoryModal
        close={historyHandlers.turnOff}
        property={historyIsOpen ? 'name' : null}
      />

      <Row gap="2">
        <span className="max-w-[95%] text-xl font-semibold phone:text-lg">{project.name}</span>

        {hasHistory && (
          <IconButton onClick={historyHandlers.turnOn} tooltipContent={t('view-name-history')}>
            <HistoryIcon color="primary" size={14} />
          </IconButton>
        )}

        {isOwner(project, loggedUser) && (
          <IconButton
            title={t('edit') as string}
            onClick={handleEditClick}
            tooltipContent={t('edit')}
          >
            <PencilIcon color="primary" size={14} />
          </IconButton>
        )}
      </Row>
    </>
  );
}
